import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { usePage } from '../../helpers/hooks';
import { can } from '../../components/Authorize';
import { CONFIG } from '../../components/Authorize/permissions/resources';
import {
  loadArrangeLayoutMap,
  normalizeCam,
  resolveArrangeLayout,
} from '../Mapping/Arrange/logic';
import { getCrowdCountScan, getCrowdCountSessions } from './api';
import {
  buildScanOptions,
  countableDetectables,
  defaultScanOption,
  fetchJson,
  flatScanOptions,
  mapPool,
  parseRekognition,
  scanIdsFromOption,
  sortTiles,
  tileBoxesKey,
} from './helpers';
import { requestPersistentStorage, unregisterLegacyAssetSw } from './signedAssetCache';

const canSelectRegions = () => can(CONFIG);

const useCrowdCount = (event, selectedEventsPage) => {
  const page = usePage();
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [sessions, setSessions] = useState([]);
  const [option, setOption] = useState(null);
  const [scans, setScans] = useState([]);
  const [tileBoxes, setTileBoxes] = useState({});
  const [rekognitionLabels, setRekognitionLabels] = useState([]);
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [showBoxes, setShowBoxes] = useState(true);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState(false);
  const [detectionsLoading, setDetectionsLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [layoutMap, setLayoutMap] = useState({});
  const [layoutsReady, setLayoutsReady] = useState(false);

  const resetDetections = () => {
    setTileBoxes({});
    setRekognitionLabels([]);
    setSelectedLabels([]);
    setDetail(null);
  };

  const reloadLayouts = useCallback(() => {
    if (!event) {
      setLayoutMap({});
      setLayoutsReady(true);
      return;
    }
    setLayoutsReady(false);
    loadArrangeLayoutMap()
      .then(setLayoutMap)
      .catch(() => setLayoutMap({}))
      .finally(() => setLayoutsReady(true));
  }, [event]);

  useEffect(() => { reloadLayouts(); }, [reloadLayouts]);

  useEffect(() => {
    unregisterLegacyAssetSw();
    requestPersistentStorage();
  }, []);

  useEffect(() => {
    if (event && (!eventId || event._id !== eventId)) {
      navigate(`/crowd-count/${event._id}`, { replace: true });
    }
  }, [event, eventId, navigate]);

  useEffect(() => {
    if (!event || selectedEventsPage !== page || event._id !== eventId) return undefined;
    let cancelled = false;
    setSessionsLoading(true);
    setSessions([]);
    setOption(null);
    setScans([]);
    resetDetections();

    getCrowdCountSessions(event.slugDate)
      .then((data) => {
        if (cancelled) return;
        const list = data?.sessions || [];
        setSessions(list);
        setOption(defaultScanOption(buildScanOptions(list, {
          includeRegions: canSelectRegions(),
        })));
      })
      .catch(() => { if (!cancelled) setSessions([]); })
      .finally(() => { if (!cancelled) setSessionsLoading(false); });

    return () => { cancelled = true; };
  }, [event, eventId, selectedEventsPage, page]);

  const optionValue = option?.value || '';
  const scanIds = useMemo(() => scanIdsFromOption(option), [option]);

  useEffect(() => {
    if (!optionValue) {
      setScans([]);
      setScanLoading(false);
      setScanError(false);
      return undefined;
    }
    if (!scanIds.length) {
      setScans([]);
      setScanLoading(false);
      setScanError(true);
      return undefined;
    }

    let cancelled = false;
    setScanLoading(true);
    setScanError(false);
    setScans([]);
    resetDetections();

    Promise.all(scanIds.map((id) => (
      getCrowdCountScan(id)
        .then((doc) => doc)
        .catch(() => null)
    )))
      .then((docs) => {
        if (cancelled) return;
        const ok = docs.filter(Boolean);
        setScans(ok);
        setScanError(ok.length === 0);
      })
      .finally(() => { if (!cancelled) setScanLoading(false); });

    return () => { cancelled = true; };
  }, [optionValue, scanIds.join(',')]);

  const isGroup = option?.kind === 'session' && scanIds.length >= 2;
  const singleScan = !isGroup ? (scans[0] || null) : null;

  const tiles = useMemo(() => {
    if (isGroup) {
      return scans.flatMap((scan) => {
        const scanId = String(scan._id);
        return sortTiles(scan.tiles).map((tile) => ({ ...tile, _scanId: scanId }));
      });
    }
    return sortTiles(singleScan?.tiles);
  }, [isGroup, scans, singleScan]);

  useEffect(() => {
    if (!tiles.length) {
      setTileBoxes({});
      setRekognitionLabels([]);
      setDetectionsLoading(false);
      return undefined;
    }
    let cancelled = false;
    setDetectionsLoading(true);

    mapPool(tiles, 6, async (tile) => {
      const key = tileBoxesKey(tile);
      const [faces, labels] = await Promise.all([
        fetchJson(tile.rekognitionFacesUrl),
        fetchJson(tile.rekognitionLabelsUrl),
      ]);
      return [key, parseRekognition(key, faces, labels)];
    }).then((entries) => {
      if (cancelled) return;
      const boxesMap = {};
      const labels = [];
      entries.forEach(([key, parsed]) => {
        boxesMap[key] = parsed.boxes;
        labels.push(...(parsed.labels || []));
      });
      setTileBoxes(boxesMap);
      setRekognitionLabels(labels);
      setDetectionsLoading(false);
    }).catch(() => {
      if (!cancelled) setDetectionsLoading(false);
    });

    return () => { cancelled = true; };
  }, [tiles]);

  const scanOptions = useMemo(
    () => buildScanOptions(sessions, { includeRegions: canSelectRegions() }),
    [sessions],
  );
  const flatOptions = useMemo(() => flatScanOptions(scanOptions), [scanOptions]);
  const detectableOptions = useMemo(() => countableDetectables(tileBoxes), [tileBoxes]);
  const selectedOptions = useMemo(
    () => detectableOptions.filter((o) => selectedLabels.includes(o.value)),
    [detectableOptions, selectedLabels],
  );
  const selectedLabelSet = useMemo(() => new Set(selectedLabels), [selectedLabels]);

  const goScan = (delta) => {
    if (flatOptions.length < 2) return;
    const i = Math.max(0, flatOptions.findIndex((o) => o.value === optionValue));
    setOption(flatOptions[(i + delta + flatOptions.length) % flatOptions.length]);
  };

  const stadiumEntries = useMemo(() => {
    if (!isGroup || !layoutsReady) return [];
    const meta = sessions.find((s) => String(s.session) === option.session);
    return scanIds.map((id) => {
      const scan = scans.find((d) => String(d._id) === id) || null;
      const region = (meta?.regions || []).find((r) => String(r._id) === id);
      const cam = normalizeCam(region?.camera || scan?.camera);
      const rid = String(region?.regionId || scan?.regionId || '').trim() || null;
      const camMeta = resolveArrangeLayout(layoutMap, cam, rid) || {};
      const hasPose = Number.isFinite(Number(camMeta.x)) && Number.isFinite(Number(camMeta.y));
      return {
        scanId: id,
        scan,
        tiles: sortTiles(scan?.tiles),
        rows: Number(scan?.rows) || Number(region?.rows) || Number(camMeta.rows) || 0,
        cols: Number(scan?.cols) || Number(region?.cols) || Number(camMeta.cols) || 0,
        tileWidth: Number(scan?.tileWidth)
          || Number(region?.tileWidth)
          || Number(camMeta.tileWidth)
          || null,
        tileHeight: Number(scan?.tileHeight)
          || Number(region?.tileHeight)
          || Number(camMeta.tileHeight)
          || null,
        label: region ? `${region.camera} · ${region.regionId}` : (scan?.camera || id),
        camera: cam,
        regionId: rid,
        arrangeLayout: hasPose
          ? { x: camMeta.x, y: camMeta.y, angle: camMeta.angle || 0 }
          : null,
      };
    });
  }, [isGroup, layoutsReady, sessions, option, scanIds, scans, layoutMap]);

  const cols = Number(singleScan?.cols) || Number(option?.cols) || 0;
  const rows = Number(singleScan?.rows) || Number(option?.rows) || 0;
  const shimmer = !isGroup && scanLoading && rows > 0 && cols > 0;
  const showRegion = shimmer || (!isGroup && !scanLoading && tiles.length > 0);
  const showStadium = isGroup && (
    scanLoading || stadiumEntries.some((e) => e.tiles.length || (e.rows && e.cols))
  );

  return {
    sessionsLoading,
    scanLoading,
    scanError,
    detectionsLoading,
    optionValue,
    hasScans: flatOptions.length > 0,
    canNavigate: flatOptions.length > 1,
    isGroup,
    scanOptions,
    option,
    setOption,
    goScan,
    detectableOptions,
    selectedOptions,
    setSelectedLabels,
    selectedLabelSet,
    showBoxes,
    setShowBoxes,
    showStadium,
    showRegion,
    shimmer,
    stadiumEntries,
    reloadLayouts,
    singleScan,
    tiles,
    rows,
    cols,
    tileBoxes,
    rekognitionLabels,
    scans,
    detail,
    setDetail,
  };
};

export default useCrowdCount;
